"use client";
import { useState } from "react";
import { useQuery } from "react-query";
import { Loader2 } from "lucide-react";
import AdminHeader from "@/components/header/admin-header";
import { ADMIN_PANEL_HEADER } from "@/constants/admin.constant";
import useChatMetricsService from "@/services/chat-metrics.service";
import { ChatMetricsChart } from "@/components/chat-metrics/chat-metrics-chart";
import { FeedbackDailyCount } from "@/interfaces/chat-metrics.interface";
import { cn } from "@/lib/utils";

const PRESETS = [
  { label: "7D", days: 7 },
  { label: "30D", days: 30 },
  { label: "90D", days: 90 },
  { label: "1Y", days: 365 },
];

const CONVERSATION_TYPES = [
  { label: "CHOP GPT", value: "CHOP_GPT" },
  { label: "Policy Assistant", value: "POLICY_GPT" },
];

const toggleButtonClass = (active: boolean) =>
  cn(
    "px-3 py-1 text-sm rounded border font-medium transition-colors",
    active
      ? "bg-[#005587] text-white border-[#005587]"
      : "text-[#005587] border-[#005587] hover:bg-[#005587]/10"
  );

const fillMissingDates = (data: FeedbackDailyCount[], days: number): FeedbackDailyCount[] => {
  const dataMap = new Map(data.map((d) => [d.date, d]));
  const filled: FeedbackDailyCount[] = [];
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  for (let i = days; i >= 0; i--) {
    const d = new Date(end);
    d.setDate(d.getDate() - i);
    // Use local (Eastern) date components to match the backend's ET grouping
    const dateStr = [
      d.getFullYear(),
      String(d.getMonth() + 1).padStart(2, "0"),
      String(d.getDate()).padStart(2, "0"),
    ].join("-");
    filled.push(dataMap.get(dateStr) ?? { date: dateStr, positive: 0, negative: 0, assistant_messages: 0, user_messages: 0, unique_users: 0 });
  }
  return filled;
};

export const ChatMetricsWrapper = () => {
  const [days, setDays] = useState(30);
  const [conversationTypes, setConversationTypes] = useState<string[]>(["POLICY_GPT"]);
  const { getDailyCounts, getTotalUniqueUsers } = useChatMetricsService();

  const { data: responseData, isLoading } = useQuery(
    ["chatMetrics", days, conversationTypes.join(",")],
    () => getDailyCounts(days, conversationTypes),
    { enabled: conversationTypes.length > 0, refetchOnMount: true }
  );

  const { data: uniqueUsersData } = useQuery(
    ["chatMetricsUniqueUsers", days, conversationTypes.join(",")],
    () => getTotalUniqueUsers(days, conversationTypes),
    { enabled: conversationTypes.length > 0, refetchOnMount: true }
  );

  const data: FeedbackDailyCount[] = fillMissingDates(
    conversationTypes.length === 0 ? [] : (responseData?.data || []),
    days
  );

  const toggleConversationType = (value: string) => {
    setConversationTypes((prev) =>
      prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]
    );
  };

  const totalPositive = data.reduce((sum, d) => sum + d.positive, 0);
  const totalNegative = data.reduce((sum, d) => sum + d.negative, 0);
  const totalAll = totalPositive + totalNegative;
  const totalQuestions = data.reduce((sum, d) => sum + (d.user_messages ?? 0), 0);
  const totalUniqueUsers = conversationTypes.length === 0 ? 0 : (uniqueUsersData?.data ?? 0);

  return (
    <div className="h-full flex flex-col bg-background text-text-500 px-4 pb-4">
      <AdminHeader header={ADMIN_PANEL_HEADER.CHAT_METRICS} />

      <div className="flex flex-wrap gap-4 my-4">
        <div className="flex gap-1">
          {PRESETS.map(({ label, days: d }) => (
            <button
              key={label}
              onClick={() => setDays(d)}
              className={toggleButtonClass(days === d)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex gap-1">
          {CONVERSATION_TYPES.map(({ label, value }) => (
            <button
              key={value}
              onClick={() => toggleConversationType(value)}
              className={toggleButtonClass(conversationTypes.includes(value))}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="bg-background border border-border rounded-md px-4 py-2 w-32 flex flex-col items-center">
          <div className="text-xs text-text-200 font-medium mb-0.5">Thumbs Up</div>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-semibold text-text-500">{totalPositive.toLocaleString()}</span>
            <span className="text-lg leading-none">👍</span>
          </div>
        </div>
        <div className="bg-background border border-border rounded-md px-4 py-2 w-32 flex flex-col items-center">
          <div className="text-xs text-text-200 font-medium mb-0.5">Thumbs Down</div>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-semibold text-text-500">{totalNegative.toLocaleString()}</span>
            <span className="text-lg leading-none">👎</span>
          </div>
        </div>
        <div className="bg-background border border-border rounded-md px-4 py-2 w-32 flex flex-col items-center">
          <div className="text-xs text-text-200 font-medium mb-0.5">Total Feedback</div>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-semibold text-text-500">{totalAll.toLocaleString()}</span>
          </div>
        </div>
        <div className="bg-background border border-border rounded-md px-4 py-2 w-32 flex flex-col items-center">
          <div className="text-xs text-text-200 font-medium mb-0.5">Questions</div>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-semibold text-text-500">{totalQuestions.toLocaleString()}</span>
          </div>
        </div>
        <div className="bg-background border border-border rounded-md px-4 py-2 w-32 flex flex-col items-center">
          <div className="text-xs text-text-200 font-medium mb-0.5">Unique Users</div>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-semibold text-text-500">{totalUniqueUsers.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-6 w-6 animate-spin text-[#005587]" />
        </div>
      ) : (
        <ChatMetricsChart data={data} />
      )}
    </div>
  );
};
